import React, { useState, useRef, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, GripVertical, Filter as FilterIcon } from "lucide-react";

import EventsSelectorMenu from "./EventsSelectorMenu";
import { getFixedPanelPositionFromRect } from "./eventSelectorGeometry";
import Icon from "shared/components/Icon";
import EventPropertyFilterModule from "features/activity/components/EventPropertyFilterModule";
import { EventPropertyFilterChip } from "features/activity/components/EventsFilterModule";
import axiosInstance from "shared/services/api/axiosInstance";

const pickerItemClass =
  "block w-full truncate px-2.5 py-1.5 text-left text-[11px] transition-colors rounded-sm";
const pickerMenuClass =
  "absolute left-0 top-full z-[130] mt-0.5 inline-block min-w-[8rem] max-w-[min(100vw-2rem,14rem)] overflow-hidden rounded-lg border py-1 shadow-lg";

/** Match FunnelQueryConfig conversion window units */
const WINDOW_UNIT_OPTIONS = [
  { value: "seconds", label: "seconds" },
  { value: "minutes", label: "minutes" },
  { value: "hours", label: "hours" },
  { value: "days", label: "days" },
  { value: "weeks", label: "weeks" },
  { value: "months", label: "months" },
];

/**
 * FlowQueryConfig — query builder for the Feature Flow feature.
 *
 * Matches the FunnelQueryConfig pattern:
 *  - Numbered event rows (unlimited events)
 *  - Per-event filter button + chips
 *  - Per anchor: User vs group accumulation + steps before/after (popover)
 *  - Conversion window + criteria (shown when ≥ 2 events)
 */
export default function FlowQueryConfig({
  anchorEvents = [],
  onAnchorEventsChange,
  conversionWindow = { value: 7, unit: "days" },
  onConversionWindowChange,
  conversionCriteria = "distinct_id",
  onConversionCriteriaChange,
  availableEvents = [],
  teamId = null,
}) {
  const [stepMenuOpen, setStepMenuOpen] = useState(null); // null | number (index) | 'add'
  const [menuAnchorRect, setMenuAnchorRect] = useState(null);
  const menuRef = useRef(null);
  const addButtonRef = useRef(null);

  // Per-event filter state
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedEventForFilter, setSelectedEventForFilter] = useState(null);
  const [selectedEventIndex, setSelectedEventIndex] = useState(null);
  const [editingFilter, setEditingFilter] = useState(null);
  const filterButtonRefs = useRef({});
  const filterChipRefs = useRef({});

  const [groupKeyOptions, setGroupKeyOptions] = useState([]);
  const [subjectMenuIndex, setSubjectMenuIndex] = useState(null);
  const [stepsPopoverIndex, setStepsPopoverIndex] = useState(null);
  const subjectWrapRefs = useRef({});
  const stepsWrapRefs = useRef({});
  const flowControlsRef = useRef(null);
  const [flowConvMenuOpen, setFlowConvMenuOpen] = useState(null); // null | 'unit' | 'criteria'

  useEffect(() => {
    const raw =
      teamId != null && teamId !== ""
        ? typeof teamId === "object" && teamId !== null
          ? teamId._id ?? teamId.id
          : teamId
        : null;
    if (raw == null || raw === "") {
      setGroupKeyOptions([]);
      return undefined;
    }
    let cancelled = false;
    axiosInstance
      .get("/group-identities/keys", { params: { team_id: raw } })
      .then((res) => {
        if (cancelled || !res.data?.success) return;
        setGroupKeyOptions(Array.isArray(res.data.keys) ? res.data.keys : []);
      })
      .catch(() => {
        if (!cancelled) setGroupKeyOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const countSubjects = useMemo(() => {
    const base = [{ id: "user", label: "User", groupKey: null }];
    const fromApi = (groupKeyOptions || []).map((k) => ({
      id: `group:${k.group_key}`,
      label: k.group_key,
      groupKey: k.group_key,
    }));
    return [...base, ...fromApi];
  }, [groupKeyOptions]);

  useEffect(() => {
    const close = (e) => {
      if (subjectMenuIndex === null && stepsPopoverIndex === null) return;
      const t = e.target;
      if (subjectMenuIndex !== null) {
        const wrap = subjectWrapRefs.current[subjectMenuIndex];
        if (wrap && wrap.contains(t)) return;
      }
      if (stepsPopoverIndex !== null) {
        const wrap = stepsWrapRefs.current[stepsPopoverIndex];
        if (wrap && wrap.contains(t)) return;
      }
      setSubjectMenuIndex(null);
      setStepsPopoverIndex(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [subjectMenuIndex, stepsPopoverIndex]);

  useEffect(() => {
    if (!flowConvMenuOpen) return;
    const close = (e) => {
      if (flowControlsRef.current?.contains(e.target)) return;
      setFlowConvMenuOpen(null);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [flowConvMenuOpen]);

  useEffect(() => {
    const handler = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(e.target)
      ) {
        setStepMenuOpen(null);
        setMenuAnchorRect(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [stepMenuOpen]);

  // ── Event management ──────────────────────────────────────────────────────

  const handleAddEvent = (eventName, kindOrSource = "event", opts = {}) => {
    const id = `anchor_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    const entityKind = (kindOrSource === 'agent' || kindOrSource === 'span') ? kindOrSource : 'event';
    const spanKind = entityKind === 'span' ? (opts.spanKind || null) : null;
    onAnchorEventsChange([
      ...anchorEvents,
      {
        id,
        eventName: eventName || "",
        entityName: eventName || "",
        entityKind,
        spanKind,
        label: eventName || "",
        stepsBefore: 0,
        stepsAfter: 3,
        countSubject: "user",
        accumulationGroupKey: null,
        filters: [],
      },
    ]);
    setStepMenuOpen(null);
    setMenuAnchorRect(null);
  };

  const handleReplaceEvent = (index, eventName, kindOrSource = "event", opts = {}) => {
    const entityKind = (kindOrSource === 'agent' || kindOrSource === 'span') ? kindOrSource : 'event';
    const spanKind = entityKind === 'span' ? (opts.spanKind || null) : null;
    const next = anchorEvents.map((a, i) =>
      i === index ? {
        ...a,
        eventName: eventName || "",
        entityName: eventName || "",
        entityKind,
        spanKind,
        label: eventName || "",
      } : a
    );
    onAnchorEventsChange(next);
    setStepMenuOpen(null);
    setMenuAnchorRect(null);
  };

  const handleRemoveEvent = (index) => {
    onAnchorEventsChange(anchorEvents.filter((_, i) => i !== index));
  };

  const handleMoveEvent = (fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= anchorEvents.length) return;
    const next = [...anchorEvents];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    onAnchorEventsChange(next);
  };

  const handleStepChange = (index, field, raw) => {
    const v = parseInt(raw, 10);
    const next = anchorEvents.map((a, i) => {
      if (i !== index) return a;
      if (field === "stepsBefore") {
        const n = Number.isFinite(v) ? Math.min(10, Math.max(0, v)) : 0;
        return { ...a, stepsBefore: n };
      }
      const n = Number.isFinite(v) ? Math.min(10, Math.max(0, v)) : 0;
      return { ...a, stepsAfter: n };
    });
    onAnchorEventsChange(next);
  };

  const handleUpdateEvent = (index, updates) => {
    const next = anchorEvents.map((a, i) => (i === index ? { ...a, ...updates } : a));
    onAnchorEventsChange(next);
  };

  const handleCountSubjectPick = (index, row) => {
    if (row.groupKey) {
      handleUpdateEvent(index, { countSubject: "group", accumulationGroupKey: row.groupKey });
    } else {
      handleUpdateEvent(index, { countSubject: "user", accumulationGroupKey: null });
    }
    setSubjectMenuIndex(null);
  };

  const isSubjectRowActive = (anchor, s) => {
    if (s.groupKey) {
      return anchor.countSubject === "group" && anchor.accumulationGroupKey === s.groupKey;
    }
    return (anchor.countSubject || "user") === "user";
  };

  const subjectLabel = (anchor) =>
    anchor.countSubject === "group" && anchor.accumulationGroupKey
      ? `Group · ${anchor.accumulationGroupKey}`
      : "User";

  // ── Per-event filter management ───────────────────────────────────────────

  const handleFilterClick = (event, eventIndex) => {
    if (!event.eventName) return;
    setSelectedEventForFilter({ id: event.eventName, eventName: event.eventName, eventType: "custom" });
    setSelectedEventIndex(eventIndex);
    setIsFilterOpen(true);
  };

  const handleFilterAdd = (filter) => {
    if (selectedEventIndex === null || !selectedEventForFilter) return;
    const currentEvent = anchorEvents[selectedEventIndex];
    const currentFilters = currentEvent.filters || [];
    const normalized = {
      id: filter.id || `ef-${Date.now()}-${Math.random()}`,
      field: filter.property || filter.field,
      property: filter.property || filter.field,
      operator: filter.operator,
      value: filter.value,
      value2: filter.value2,
      propertyType: filter.propertyType,
      propertySource: filter.propertySource,
      propertyLabel: filter.propertyLabel || filter.property || filter.field,
      category: "event_properties",
      eventName: selectedEventForFilter.eventName,
      listMode: filter.listMode,
      listItemType: filter.listItemType,
      sampleValues: filter.sampleValues || [],
      connector: currentFilters.length === 0 ? "WHERE" : "AND",
    };
    if (editingFilter) {
      handleUpdateEvent(selectedEventIndex, {
        filters: currentFilters.map((f) => (f.id === editingFilter.id ? normalized : f)),
      });
      setEditingFilter(null);
    } else {
      handleUpdateEvent(selectedEventIndex, { filters: [...currentFilters, normalized] });
    }
    setIsFilterOpen(false);
    setSelectedEventForFilter(null);
    setSelectedEventIndex(null);
  };

  const handleFirstTimeChange = (enabled) => {
    if (selectedEventIndex === null) return;
    const currentFilters = anchorEvents[selectedEventIndex]?.filters || [];
    if (enabled) {
      const without = currentFilters.filter((f) => f.category !== "first_time");
      handleUpdateEvent(selectedEventIndex, {
        filters: [
          ...without,
          {
            id: "first-time-filter",
            field: "firstTime",
            property: "firstTime",
            operator: "equals",
            value: true,
            category: "first_time",
            eventName: selectedEventForFilter?.eventName,
            connector: without.length === 0 ? "WHERE" : "AND",
          },
        ],
      });
    } else {
      handleUpdateEvent(selectedEventIndex, {
        filters: currentFilters.filter((f) => f.category !== "first_time"),
      });
    }
    setIsFilterOpen(false);
    setSelectedEventForFilter(null);
    setSelectedEventIndex(null);
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const eventsList = Array.isArray(availableEvents) ? availableEvents : [];
  const hasMultiple = anchorEvents.length >= 2;

  const CRITERIA_OPTIONS = [
    { value: "distinct_id", label: "By Distinct ID" },
    { value: "totals", label: "Totals" },
    { value: "sessions", label: "By Sessions" },
  ];

  const cwRaw = conversionWindow && typeof conversionWindow === "object" ? conversionWindow : {};
  let cwValue = parseInt(String(cwRaw.value ?? 7), 10);
  if (!Number.isFinite(cwValue) || cwValue < 1) cwValue = 7;
  const cwUnit = typeof cwRaw.unit === "string" && cwRaw.unit ? cwRaw.unit : "days";
  const conversionWindowResolved = { value: cwValue, unit: cwUnit };

  const setConversionWindow = (patch) => {
    onConversionWindowChange({
      ...conversionWindowResolved,
      ...patch,
    });
  };

  const criteriaLabel =
    CRITERIA_OPTIONS.find((o) => o.value === conversionCriteria)?.label || "By Distinct ID";

  return (
    <>
      {/* Section header: match FunnelQueryConfig “Funnel steps” label */}
      <div className="mb-2">
        <span className="text-sm font-medium text-ink-muted">Events</span>
      </div>

      {/* Event rows: compact stacked list like funnel steps */}
      {anchorEvents.length === 0 ? (
        <div
          className="mb-4 rounded-lg border border-dashed px-2.5 py-2 text-center text-[11px] leading-snug"
          style={{ borderColor: "var(--border-default)", color: "var(--text-muted)" }}
        >
          Add an event below to start.
        </div>
      ) : (
        <div
          className="mb-4 space-y-1 rounded-lg border p-2"
          style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-base)" }}
        >
          {anchorEvents.map((anchor, index) => {
            const eventFilters = (anchor.filters || []).filter((f) => f.category !== "first_time");
            const hasFilters = eventFilters.length > 0;

            return (
              <div key={anchor.id || index} className="space-y-1">
                <div
                  className="group/flowrow space-y-0.5 rounded-md px-1.5 py-0.5 transition-colors hover:bg-surface-inverse/[0.04]"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    const dragIndex = parseInt(e.dataTransfer.getData("text/plain"), 10);
                    if (dragIndex !== index) handleMoveEvent(dragIndex, index);
                  }}
                >
                  <div
                    className="relative flex min-w-0 flex-nowrap items-center gap-1.5 text-xs"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.effectAllowed = "move";
                      e.dataTransfer.setData("text/plain", index.toString());
                      e.currentTarget.style.opacity = "0.5";
                    }}
                    onDragEnd={(e) => {
                      e.currentTarget.style.opacity = "1";
                    }}
                  >
                    <div className="flex-shrink-0 cursor-move text-ink-disabled hover:text-ink-secondary">
                      <GripVertical className="h-3.5 w-3.5" />
                    </div>
                    <div
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-dotted text-[11px] font-semibold tabular-nums"
                      style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
                      aria-hidden
                    >
                      {index + 1}
                    </div>
                    <span
                      className="min-w-0 flex-1 cursor-pointer truncate text-[12px] font-medium leading-snug"
                      style={{ color: anchor.eventName ? "var(--text-secondary)" : "var(--text-muted)" }}
                      onClick={(e) => {
                        if (stepMenuOpen === index) {
                          setStepMenuOpen(null);
                          setMenuAnchorRect(null);
                        } else {
                          setMenuAnchorRect(getFixedPanelPositionFromRect(e.currentTarget.getBoundingClientRect()));
                          setStepMenuOpen(index);
                        }
                      }}
                      title={anchor.eventName ? "Change event" : "Select event"}
                    >
                      {anchor.label || anchor.eventName || "Select event"}
                    </span>

                    <div className="ml-auto flex shrink-0 items-center gap-0 opacity-0 transition-opacity group-hover/flowrow:opacity-100 group-focus-within/flowrow:opacity-100">
                      {anchor.eventName && (
                        <button
                          ref={(el) => { filterButtonRefs.current[`ev-${index}`] = el; }}
                          type="button"
                          onClick={() => handleFilterClick(anchor, index)}
                          className="rounded p-0.5 hover:opacity-70"
                          title="Filter"
                        >
                          <FilterIcon size={14} color={hasFilters ? "var(--brand-primary)" : "var(--text-muted)"} />
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRemoveEvent(index)}
                        className="rounded p-0.5 hover:opacity-70"
                        title="Remove event"
                      >
                        <Icon name="delete" size={14} color="var(--text-muted)" />
                      </button>
                    </div>
                  </div>

                  {/* User / Group + steps summary: single line; popover aligns right to avoid viewport overflow */}
                  <div
                    className="ml-11 flex min-w-0 flex-nowrap items-center gap-2"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div
                      className="relative shrink-0"
                      ref={(el) => {
                        subjectWrapRefs.current[index] = el;
                      }}
                    >
                      <button
                        type="button"
                        aria-expanded={subjectMenuIndex === index}
                        aria-haspopup="listbox"
                        aria-label="Accumulate by user or group"
                        onClick={() => {
                          setStepsPopoverIndex(null);
                          setFlowConvMenuOpen(null);
                          setSubjectMenuIndex((m) => (m === index ? null : index));
                        }}
                        className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
                        style={{ fontSize: "11px" }}
                      >
                        {subjectLabel(anchor)}
                        <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
                      </button>
                      {subjectMenuIndex === index && (
                        <div
                          role="listbox"
                          className={`${pickerMenuClass} shadow-token-lg`}
                          style={{
                            backgroundColor: "var(--surface-base)",
                            borderColor: "var(--border-default)",
                          }}
                        >
                          {countSubjects.map((s) => {
                            const active = isSubjectRowActive(anchor, s);
                            return (
                              <button
                                key={s.id}
                                type="button"
                                role="option"
                                aria-selected={active}
                                title={s.label}
                                onClick={() => handleCountSubjectPick(index, s)}
                                className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                                style={{
                                  color: active ? "var(--text-primary)" : "var(--text-secondary)",
                                  backgroundColor: "transparent",
                                }}
                                onMouseEnter={(ev) => {
                                  ev.currentTarget.style.backgroundColor = "var(--surface-hover)";
                                }}
                                onMouseLeave={(ev) => {
                                  ev.currentTarget.style.backgroundColor = "transparent";
                                }}
                              >
                                {s.label}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div
                      className="relative min-w-0 flex-1"
                      ref={(el) => {
                        stepsWrapRefs.current[index] = el;
                      }}
                    >
                      <button
                        type="button"
                        aria-expanded={stepsPopoverIndex === index}
                        aria-haspopup="dialog"
                        aria-label="Edit steps before and after"
                        onClick={() => {
                          setSubjectMenuIndex(null);
                          setFlowConvMenuOpen(null);
                          setStepsPopoverIndex((p) => (p === index ? null : index));
                        }}
                        className="inline-flex w-full min-w-0 max-w-full items-center gap-1 rounded-full border px-2.5 py-0.5 text-left text-[11px] font-medium leading-snug transition-colors hover:opacity-95"
                        style={{
                          borderColor: "color-mix(in srgb, var(--brand-primary) 30%, transparent)",
                          backgroundColor: "color-mix(in srgb, var(--brand-primary) 10%, transparent)",
                          color: "var(--brand-primary)",
                        }}
                      >
                        <span className="min-w-0 truncate">
                          {anchor.stepsBefore ?? 0} Steps before, {anchor.stepsAfter ?? 3} Steps after
                        </span>
                        <ChevronDown className="h-3 w-3 shrink-0 opacity-70" aria-hidden />
                      </button>
                      {stepsPopoverIndex === index && (
                        <div
                          role="dialog"
                          aria-label="Steps"
                          className="absolute right-0 top-full z-[130] mt-1 box-border w-[min(17rem,calc(100vw-1.5rem))] max-w-[100%] rounded-lg border p-3 shadow-token-lg"
                          style={{ borderColor: "var(--border-default)", backgroundColor: "var(--surface-base)" }}
                        >
                          <div className="text-sm font-semibold text-ink">Steps</div>
                          <p className="mt-0.5 text-[11px] leading-snug" style={{ color: "var(--text-muted)" }}>
                            Choose the number of steps to show before and after the event.
                          </p>
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <div className="mb-1 text-[11px] font-medium" style={{ color: "var(--text-muted)" }}>
                                Before
                              </div>
                              <input
                                type="number"
                                min={0}
                                max={10}
                                value={anchor.stepsBefore ?? 0}
                                onChange={(e) => handleStepChange(index, "stepsBefore", e.target.value)}
                                className="w-full rounded-md border px-2 py-1.5 text-sm tabular-nums"
                                style={{
                                  borderColor: "var(--border-default)",
                                  background: "var(--surface-base)",
                                  color: "var(--text-primary)",
                                }}
                              />
                            </div>
                            <div>
                              <div className="mb-1 text-[11px] font-medium" style={{ color: "var(--text-muted)" }}>
                                After
                              </div>
                              <input
                                type="number"
                                min={0}
                                max={10}
                                value={anchor.stepsAfter ?? 3}
                                onChange={(e) => handleStepChange(index, "stepsAfter", e.target.value)}
                                className="w-full rounded-md border px-2 py-1.5 text-sm tabular-nums"
                                style={{
                                  borderColor: "var(--border-default)",
                                  background: "var(--surface-base)",
                                  color: "var(--text-primary)",
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Filter chips: aligned with funnel step chips */}
                {hasFilters && (
                  <div className="ml-11 space-y-1">
                    {eventFilters.map((filter, fi) => {
                      if (!filterChipRefs.current[filter.id]) {
                        filterChipRefs.current[filter.id] = React.createRef();
                      }
                      return (
                        <EventPropertyFilterChip
                          key={filter.id}
                          filter={filter}
                          compact
                          isFirst={fi === 0}
                          filterRef={filterChipRefs.current[filter.id]}
                          onEdit={() => {
                            setEditingFilter(filter);
                            setSelectedEventForFilter({ id: anchor.eventName, eventName: anchor.eventName, eventType: "custom" });
                            setSelectedEventIndex(index);
                            setIsFilterOpen(true);
                          }}
                          onUpdate={(updates) => {
                            handleUpdateEvent(index, {
                              filters: (anchor.filters || []).map((f) =>
                                f.id === filter.id ? { ...f, ...updates } : f
                              ),
                            });
                          }}
                          onRemove={() => {
                            const updated = (anchor.filters || [])
                              .filter((f) => f.id !== filter.id)
                              .map((f, idx) => ({ ...f, connector: idx === 0 ? "WHERE" : f.connector || "AND" }));
                            handleUpdateEvent(index, { filters: updated });
                          }}
                          teamId={teamId}
                          eventName={anchor.eventName}
                        />
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Portal-rendered event selector (add + replace) */}
      {menuAnchorRect && (typeof stepMenuOpen === "number" || stepMenuOpen === "add") &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              top: menuAnchorRect.top,
              left: menuAnchorRect.left,
              width: menuAnchorRect.width,
              maxHeight: menuAnchorRect.maxHeightPx ?? 480,
              zIndex: 9999,
            }}
            className="flex max-h-[min(65vh,calc(100vh-24px))] flex-col overflow-hidden"
          >
            <EventsSelectorMenu
              availableEvents={eventsList}
              onSelect={(name, kind, opts) =>
                typeof stepMenuOpen === "number"
                  ? handleReplaceEvent(stepMenuOpen, name, kind, opts)
                  : handleAddEvent(name, kind, opts)
              }
              onClose={() => { setStepMenuOpen(null); setMenuAnchorRect(null); }}
              maxHeight={menuAnchorRect.maxHeightPx ?? 480}
            />
          </div>,
          document.body
        )}

      {/* Add event: match funnel “Add step” control */}
      <div className="relative mb-6 mt-1">
        <button
          ref={addButtonRef}
          type="button"
          onClick={() => {
            if (stepMenuOpen === "add") {
              setStepMenuOpen(null);
              setMenuAnchorRect(null);
            } else {
              const el = addButtonRef.current;
              if (el) setMenuAnchorRect(getFixedPanelPositionFromRect(el.getBoundingClientRect()));
              setStepMenuOpen("add");
            }
          }}
          className="flex w-full items-center gap-2 rounded-lg border border-dashed px-2.5 py-1.5 text-left text-xs transition-colors"
          style={{ borderColor: "var(--border-default)", color: "var(--text-muted)" }}
        >
          <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center font-medium text-ink-muted">
            +
          </span>
          <span className="min-w-0 flex-1">Add event</span>
          <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-70" />
        </button>
      </div>

      {/* Conversion window + count by: compact inline copy (match FunnelQueryConfig) */}
      {hasMultiple && (
        <div ref={flowControlsRef} className="mt-4 space-y-2">
          <p
            className="flex min-w-0 flex-nowrap items-center gap-x-2 text-[12px] leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            <span className="flex min-w-0 flex-nowrap items-baseline gap-x-1">
              <span>Within</span>
              <input
                type="number"
                min={1}
                value={conversionWindowResolved.value}
                onChange={(e) => {
                  const raw = e.target.value;
                  const value = parseInt(raw, 10);
                  if (raw === "" || !Number.isFinite(value)) return;
                  setConversionWindow({ value: Math.max(1, value) });
                }}
                onBlur={(e) => {
                  const value = parseInt(e.target.value, 10);
                  if (!Number.isFinite(value) || value < 1) {
                    setConversionWindow({ value: 7 });
                  }
                }}
                className="min-w-[2rem] max-w-[3.5rem] border-0 border-b border-dashed bg-transparent px-0 py-0 text-center text-[12px] font-semibold tabular-nums outline-none focus:border-solid"
                style={{ borderColor: "var(--border-default)", color: "var(--text-primary)" }}
                aria-label="Conversion window amount"
              />
              <span className="relative inline-flex items-center">
                <button
                  type="button"
                  aria-expanded={flowConvMenuOpen === "unit"}
                  aria-haspopup="listbox"
                  aria-label="Conversion window unit"
                  onClick={() => {
                    setSubjectMenuIndex(null);
                    setStepsPopoverIndex(null);
                    setFlowConvMenuOpen((m) => (m === "unit" ? null : "unit"));
                  }}
                  className="inline-flex items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug hover:opacity-80"
                  style={{ fontSize: "12px", color: "var(--text-primary)" }}
                >
                  {(WINDOW_UNIT_OPTIONS.find((u) => u.value === conversionWindowResolved.unit) || {
                    label: conversionWindowResolved.unit,
                  }).label}
                  <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
                </button>
                {flowConvMenuOpen === "unit" && (
                  <div
                    role="listbox"
                    className={`${pickerMenuClass} shadow-token-lg`}
                    style={{
                      backgroundColor: "var(--surface-base)",
                      borderColor: "var(--border-default)",
                    }}
                  >
                    {WINDOW_UNIT_OPTIONS.map((u) => {
                      const active = u.value === conversionWindowResolved.unit;
                      return (
                        <button
                          key={u.value}
                          type="button"
                          role="option"
                          aria-selected={active}
                          onClick={() => {
                            setConversionWindow({ unit: u.value });
                            setFlowConvMenuOpen(null);
                          }}
                          className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                          style={{
                            color: active ? "var(--text-primary)" : "var(--text-secondary)",
                            backgroundColor: "transparent",
                          }}
                          onMouseEnter={(ev) => {
                            ev.currentTarget.style.backgroundColor = "var(--surface-hover)";
                          }}
                          onMouseLeave={(ev) => {
                            ev.currentTarget.style.backgroundColor = "transparent";
                          }}
                        >
                          {u.label}
                        </button>
                      );
                    })}
                  </div>
                )}
              </span>
            </span>
          </p>

          <div
            className="flex min-w-0 flex-nowrap items-center gap-x-2 border-t border-dotted pt-2"
            style={{ borderColor: "var(--border-default)" }}
          >
            <span className="shrink-0 text-[12px] leading-relaxed" style={{ color: "var(--text-secondary)" }}>
              Count by
            </span>
            <div className="relative min-w-0 flex-1">
              <button
                type="button"
                aria-expanded={flowConvMenuOpen === "criteria"}
                aria-haspopup="listbox"
                aria-label="Conversion criteria"
                onClick={() => {
                  setSubjectMenuIndex(null);
                  setStepsPopoverIndex(null);
                  setFlowConvMenuOpen((m) => (m === "criteria" ? null : "criteria"));
                }}
                className="inline-flex max-w-full items-center gap-0.5 border-0 bg-transparent p-0 font-semibold leading-snug text-ink hover:opacity-80"
                style={{ fontSize: "11px" }}
              >
                <span className="min-w-0 truncate">{criteriaLabel}</span>
                <ChevronDown className="h-3 w-3 shrink-0 opacity-55" strokeWidth={2.25} aria-hidden />
              </button>
              {flowConvMenuOpen === "criteria" && (
                <div
                  role="listbox"
                  className={`${pickerMenuClass} shadow-token-lg`}
                  style={{
                    backgroundColor: "var(--surface-base)",
                    borderColor: "var(--border-default)",
                  }}
                >
                  {CRITERIA_OPTIONS.map((opt) => {
                    const active = opt.value === conversionCriteria;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        role="option"
                        aria-selected={active}
                        onClick={() => {
                          onConversionCriteriaChange(opt.value);
                          setFlowConvMenuOpen(null);
                        }}
                        className={`${pickerItemClass} ${active ? "font-semibold" : "font-medium"}`}
                        style={{
                          color: active ? "var(--text-primary)" : "var(--text-secondary)",
                          backgroundColor: "transparent",
                        }}
                        onMouseEnter={(ev) => {
                          ev.currentTarget.style.backgroundColor = "var(--surface-hover)";
                        }}
                        onMouseLeave={(ev) => {
                          ev.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Per-event filter picker */}
      {isFilterOpen && selectedEventForFilter && (
        <EventPropertyFilterModule
          isOpen={isFilterOpen}
          onClose={() => {
            setIsFilterOpen(false);
            setSelectedEventForFilter(null);
            setSelectedEventIndex(null);
            setEditingFilter(null);
          }}
          triggerRef={
            editingFilter && filterChipRefs.current[editingFilter.id]
              ? filterChipRefs.current[editingFilter.id]
              : filterButtonRefs.current[`ev-${selectedEventIndex}`]
          }
          eventName={selectedEventForFilter.eventName}
          eventId={selectedEventForFilter.id}
          teamId={teamId}
          filters={selectedEventIndex !== null ? (anchorEvents[selectedEventIndex]?.filters || []) : []}
          onFiltersChange={handleFilterAdd}
          existingFilter={editingFilter}
          firstTimeOnly={
            selectedEventIndex !== null
              ? (anchorEvents[selectedEventIndex]?.filters || []).some((f) => f.category === "first_time")
              : false
          }
          onFirstTimeChange={handleFirstTimeChange}
        />
      )}
    </>
  );
}
